import ButtonLink from "../components/ButtonLink";
import "./PressPage.css";

const imagePackUrl =
  "https://www.dropbox.com/scl/fo/6r0ius2usmjs1llgaioqk/ANWNXANcGiKkQnXuJYbKglY?rlkey=i9lm4ndfurlrc2plvp59ev5zx&st=8h32afpr&dl=0";

const featuredAsset = {
  title: "AI-guided CRISPR preview",
  category: "Featured screenshot",
  dimensions: "2500 x 1458",
  src: "/assets/medical/ai-guide-2500.webp",
  alt: "10k Science web preview showing an AI-guided CRISPR and sickle cell experience",
};

const pressAssets = [
  {
    title: "Human anatomy environment",
    category: "3D render",
    dimensions: "3840 x 2160",
    src: "/assets/medical/anatomy-hero-3840.webp",
    alt: "Human anatomy with skeleton and circulatory system visible",
    size: "wide",
  },
  {
    title: "CRISPR molecule close-up",
    category: "3D render",
    dimensions: "3840 x 2160",
    src: "/assets/medical/crispr-molecule-3840.webp",
    alt: "CRISPR protein editing a strand of DNA",
    size: "wide",
  },
  {
    title: "Neuron network",
    category: "3D render",
    dimensions: "3840 x 2046",
    src: "/assets/medical/neurons-3840.webp",
    alt: "A network of neurons with illuminated signal paths",
    size: "wide",
  },
  {
    title: "Sickle cell blood vessel",
    category: "3D render",
    dimensions: "3840 x 2046",
    src: "/assets/medical/sickle-cells-3840.webp",
    alt: "Healthy and sickled red blood cells moving through a blood vessel",
    size: "wide",
  },
  {
    title: "Response distribution",
    category: "Dashboard",
    dimensions: "2800 x 1605",
    src: "/assets/medical/dashboard-responses-2800.webp",
    alt: "10k Science dashboard showing response distribution and learner responses",
  },
  {
    title: "Learner session detail",
    category: "Dashboard",
    dimensions: "2800 x 1676",
    src: "/assets/medical/dashboard-session-2800.webp",
    alt: "10k Science session dashboard showing learner activity and AI feedback",
  },
  {
    title: "iOS experience",
    category: "Platform",
    dimensions: "2560 x 1440",
    src: "/assets/medical/platform-ios-2560.webp",
    alt: "Molecular science content running on an iOS tablet",
  },
  {
    title: "VR experience",
    category: "Platform",
    dimensions: "2500 x 1198",
    src: "/assets/medical/platform-vr-2500.webp",
    alt: "10k Science running with a Meta Quest headset and controllers",
    contain: true,
  },
  {
    title: "Interactive web preview",
    category: "Screenshot",
    dimensions: "2500 x 1406",
    src: "/assets/original/images/screenshot4.png",
    alt: "10k Science web experience showing a close-up of an insect environment",
  },
  {
    title: "Microscopic cell scene",
    category: "Screenshot",
    dimensions: "2000 x 1125",
    src: "/assets/original/images/Screenshot10-22@04.42.39.jpg",
    alt: "Microscopic 10k Science scene with cellular structures",
  },
  {
    title: "Blood vessel scene",
    category: "Screenshot",
    dimensions: "2000 x 1125",
    src: "/assets/original/images/10k Science Blood Vessel.png",
    alt: "10k Science blood vessel scene",
  },
  {
    title: "Plant cell scene",
    category: "Screenshot",
    dimensions: "2000 x 938",
    src: "/assets/original/images/10k Science Plant Cell.jpg",
    alt: "10k Science plant cell scene",
  },
];

const fileNameFromPath = (path) =>
  decodeURIComponent(path.split("/").pop() || "10k-science-press-asset");

function PressAssetCard({ asset, eager = false }) {
  const cardClasses = [
    "press-asset",
    asset.size === "wide" ? "press-asset--wide" : "",
    asset.contain ? "press-asset--contain" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <figure className={cardClasses}>
      <a
        aria-label={`Open ${asset.title} full-resolution image`}
        className="press-asset__image-link"
        href={asset.src}
        rel="noreferrer"
        target="_blank"
      >
        <img
          alt={asset.alt}
          decoding="async"
          fetchPriority={eager ? "high" : undefined}
          loading={eager ? "eager" : "lazy"}
          src={asset.src}
        />
      </a>
      <figcaption>
        <div>
          <p>{asset.category}</p>
          <h2>{asset.title}</h2>
          <span>{asset.dimensions}</span>
        </div>
        <a
          className="press-asset__download"
          download={fileNameFromPath(asset.src)}
          href={asset.src}
        >
          Download
        </a>
      </figcaption>
    </figure>
  );
}

export default function PressPage() {
  return (
    <article className="press-page">
      <section className="press-hero">
        <a
          aria-label={`Open ${featuredAsset.title} full-resolution image`}
          className="press-hero__image-link"
          href={featuredAsset.src}
          rel="noreferrer"
          target="_blank"
        >
          <img
            alt={featuredAsset.alt}
            className="press-hero__image"
            fetchPriority="high"
            src={featuredAsset.src}
          />
        </a>
        <div className="content-width press-hero__content">
          <p className="press-kicker">Press assets</p>
          <h1>High-resolution visuals</h1>
          <div className="press-hero__actions">
            <ButtonLink href={imagePackUrl} size="small" target="_blank">
              Download image pack
            </ButtonLink>
            <ButtonLink
              href="mailto:info@dynamoid.com"
              size="small"
              variant="outline"
            >
              Contact
            </ButtonLink>
          </div>
          <p className="press-hero__meta">
            {featuredAsset.category} / {featuredAsset.dimensions}
          </p>
        </div>
      </section>

      <section
        aria-labelledby="press-gallery-title"
        className="press-gallery-section"
      >
        <div className="content-width">
          <header className="press-gallery-header">
            <p className="press-kicker">Gallery</p>
            <h2 id="press-gallery-title">Screenshots and renders</h2>
          </header>
          <div className="press-gallery">
            {pressAssets.map((asset, index) => (
              <PressAssetCard asset={asset} eager={index < 2} key={asset.src} />
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
